/**
 * 投票 / 评选类大屏舞台：现场投票、评委评分、观点PK、表决共用。
 * 数据来自 snapshot.gameData（votes / options / label）
 */
<template>
  <div class="vote-stage">
    <div class="vote-stage__bg" aria-hidden="true">
      <div class="vote-stage__glow vote-stage__glow--1" />
      <div class="vote-stage__glow vote-stage__glow--2" />
    </div>

    <div class="vote-stage__head">
      <div class="vote-stage__banner">{{ label }}</div>
      <div class="vote-stage__stats">
        <span class="vote-stat">已投票 <em>{{ total }}</em> 票</span>
        <span class="vote-stat">已入场 <em>{{ onlineCount }}</em> 人</span>
        <span class="vote-stat is-live">实时统计</span>
      </div>
    </div>

    <div class="vote-list">
      <div
        v-for="(row, i) in rows"
        :key="row.option"
        class="vote-row"
        :class="{ 'is-leader': row.leader && total > 0 }"
      >
        <span class="vote-rank" :class="'is-no' + (i + 1)">{{ i + 1 }}</span>
        <span class="vote-name">{{ row.option }}</span>
        <div class="vote-bar">
          <div class="vote-bar__fill" :style="{ width: row.percent + '%' }">
            <span v-if="row.percent > 8" class="vote-bar__percent">{{ row.percent }}%</span>
          </div>
        </div>
        <span class="vote-count">{{ row.count }} 票</span>
      </div>
      <div v-if="!total" class="vote-empty">等待观众投票…</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VoteScreen',
  props: {
    gameData: { type: Object, default: () => ({}) },
    onlineCount: { type: Number, default: 0 },
    title: { type: String, default: '' }
  },
  computed: {
    options() {
      const gd = this.gameData || {}
      return Array.isArray(gd.options) && gd.options.length ? gd.options : ['支持', '反对', '弃权']
    },
    votes() {
      return (this.gameData && this.gameData.votes) || {}
    },
    total() {
      let sum = 0
      this.options.forEach((opt, i) => { sum += this.countOf(opt, i) })
      return sum
    },
    rows() {
      const list = this.options.map((opt, i) => ({
        option: opt,
        count: this.countOf(opt, i),
        percent: this.percent(opt, i)
      }))
      const max = Math.max.apply(null, list.map(r => r.count).concat([0]))
      list.forEach(r => { r.leader = max > 0 && r.count === max })
      return list
    },
    label() {
      return this.title || (this.gameData && this.gameData.label) || '现场投票'
    }
  },
  methods: {
    /** 后端 votes 以选项文本为键；兼容历史按索引存的数据 */
    countOf(option, index) {
      const votes = this.votes || {}
      const byText = votes[option]
      if (byText != null) return Number(byText) || 0
      const byIndex = votes[index] != null ? votes[index] : votes[String(index)]
      return Number(byIndex) || 0
    },
    percent(option, index) {
      if (!this.total) return 0
      return Math.round(this.countOf(option, index) / this.total * 100)
    }
  }
}
</script>

<style scoped>
.vote-stage {
  position: absolute;
  inset: 0;
  overflow: hidden;
  color: #f5f7fa;
}
.vote-stage__bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 42%, #2a1740 0%, #140c24 62%, #06040e 100%);
  overflow: hidden;
}
.vote-stage__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.45;
}
.vote-stage__glow--1 {
  width: 38vw;
  height: 38vw;
  left: 4%;
  top: 8%;
  background: rgba(139, 92, 246, 0.45);
  animation: voteFloat 9s ease-in-out infinite alternate;
}
.vote-stage__glow--2 {
  width: 32vw;
  height: 32vw;
  right: 4%;
  bottom: 6%;
  background: rgba(255, 170, 80, 0.32);
  animation: voteFloat 11s ease-in-out infinite alternate-reverse;
}
.vote-stage__head {
  position: relative;
  z-index: 3;
  padding-top: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.vote-stage__banner {
  display: inline-block;
  padding: 8px 36px;
  border-radius: 24px;
  background: linear-gradient(90deg, #7b3cff, #c84bff);
  font-size: 28px;
  letter-spacing: 4px;
  box-shadow: 0 0 24px rgba(160, 80, 255, 0.55);
}
.vote-stage__stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}
.vote-stat {
  padding: 5px 16px;
  border-radius: 999px;
  font-size: 16px;
  color: #e9e3ff;
  background: rgba(14, 8, 26, 0.55);
  border: 1px solid rgba(180, 140, 255, 0.28);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.85);
}
.vote-stat em {
  font-style: normal;
  font-weight: 800;
  color: #ffe6a0;
  font-variant-numeric: tabular-nums;
}
.vote-stat.is-live {
  color: #9ff0d0;
  border-color: rgba(80, 220, 170, 0.45);
}
.vote-list {
  position: absolute;
  left: 50%;
  top: 160px;
  bottom: 24px;
  transform: translateX(-50%);
  width: min(1200px, 88vw);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  z-index: 2;
}
.vote-row {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 10px 18px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.vote-row.is-leader {
  background: linear-gradient(90deg, rgba(255, 205, 100, 0.16), rgba(255, 255, 255, 0.04));
  border-color: rgba(255, 214, 102, 0.55);
  box-shadow: 0 0 28px rgba(255, 200, 90, 0.18);
}
.vote-rank {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 800;
  color: #cbd5e1;
  background: rgba(255, 255, 255, 0.12);
}
.vote-rank.is-no1 {
  color: #3b2600;
  background: linear-gradient(135deg, #ffe9a6, #ffc247);
}
.vote-rank.is-no2 {
  color: #1d2733;
  background: linear-gradient(135deg, #e8f0ff, #a9bfe0);
}
.vote-rank.is-no3 {
  color: #3a1c08;
  background: linear-gradient(135deg, #ffd3a6, #e5914d);
}
.vote-name {
  width: 180px;
  flex-shrink: 0;
  font-size: 26px;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vote-bar {
  flex: 1;
  height: 34px;
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.vote-bar__fill {
  height: 100%;
  min-width: 0;
  border-radius: 17px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 10px;
  background: linear-gradient(90deg, #409eff, #79bbff);
  transition: width 0.45s ease;
}
.vote-row.is-leader .vote-bar__fill {
  background: linear-gradient(90deg, #ff9f1a, #ffd76a);
}
.vote-bar__percent {
  font-size: 16px;
  font-weight: 700;
  color: rgba(6, 12, 24, 0.82);
}
.vote-count {
  width: 130px;
  flex-shrink: 0;
  font-size: 22px;
  font-variant-numeric: tabular-nums;
  color: #ffe6a0;
}
.vote-empty {
  text-align: center;
  font-size: 26px;
  opacity: 0.55;
  padding: 40px 0;
}
@keyframes voteFloat {
  from { transform: translate(0, 0) scale(1); }
  to { transform: translate(3%, 4%) scale(1.12); }
}
</style>
